/**
 * The utilisation bar — one implementation, used by the workload table and the
 * member profile (V5-B0, carried over from V5-08 §2).
 *
 * Extracted rather than copied: two bars that disagreed about where "over
 * capacity" starts would be worse than no bar on the profile at all, because
 * the same person would look fine on one screen and overloaded on another.
 */

import { formatMinutes } from '@/lib/utils';

export function utilisationColour(percentage: number): string {
  if (percentage > 100) return 'var(--danger)';
  if (percentage > 80) return 'var(--warn)';
  return 'var(--accent)';
}

export interface WorkloadMeterProps {
  utilisation: number;
  assignedMinutes: number;
  capacityMinutes: number;
  /** Bar width; the workload table constrains it, the profile lets it fill. */
  className?: string;
  showTotals?: boolean;
}

export function WorkloadMeter({
  utilisation,
  assignedMinutes,
  capacityMinutes,
  className,
  showTotals = false,
}: WorkloadMeterProps) {
  return (
    <div className="flex items-center gap-2" data-testid="workload-meter">
      <div
        className={`h-2 w-full overflow-hidden rounded-full bg-[var(--surface-2)] ${className ?? ''}`}
      >
        <div
          className="h-full rounded-full transition-[width] duration-[180ms]"
          style={{
            // Capped at 100% so the bar stays a bar; the number beside it
            // carries the overload.
            width: `${Math.min(utilisation, 100)}%`,
            background: utilisationColour(utilisation),
          }}
        />
      </div>
      <span
        className="vt-mono w-[46px] text-right text-[12px] font-medium"
        style={{ color: utilisationColour(utilisation) }}
        data-testid="workload-utilisation"
      >
        {/* A capacity of zero divides into infinity; say so rather than print a
            six-digit percentage. */}
        {utilisation > 900 ? '∞' : `${utilisation}%`}
      </span>
      {showTotals && (
        <span className="vt-mono whitespace-nowrap text-[12px] vt-muted" data-testid="workload-totals">
          {formatMinutes(assignedMinutes)} / {formatMinutes(capacityMinutes)}
        </span>
      )}
    </div>
  );
}
